import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bot, X, Send, Loader2, MessageCircle } from "lucide-react";
import { buildWaLink } from "@/lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "bah_ai_session_id";
const ROLE_KEY = "bah_ai_role_hint";

const ROLE_OPTIONS = [
  { id: "vet", label: "Veterinarian" },
  { id: "agrovet", label: "Agrovet / Stockist" },
  { id: "farmer", label: "Farmer" },
  { id: "distributor", label: "Distributor / Institution" },
];

const WELCOME = {
  role: "assistant",
  content:
    "Hi — I'm the Biodeal Animal Health assistant. I can suggest products from our catalogue, explain instruments and cold-chain options, and route you to our technical team on WhatsApp. What are you working on today?",
};

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(
    typeof window !== "undefined"
      ? window.localStorage.getItem(STORAGE_KEY) || ""
      : ""
  );
  const [roleHint, setRoleHint] = useState(
    typeof window !== "undefined"
      ? window.localStorage.getItem(ROLE_KEY) || ""
      : ""
  );
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scroller = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollTop = scroller.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/ai/chat`, {
        session_id: sessionId || undefined,
        message: text,
        role_hint: roleHint || undefined,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        window.localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "I couldn't reach the assistant just now. Please WhatsApp our technical team at +254 706 333 829.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const chooseRole = (id) => {
    setRoleHint(id);
    window.localStorage.setItem(ROLE_KEY, id);
  };

  const resetChat = () => {
    setSessionId("");
    window.localStorage.removeItem(STORAGE_KEY);
    setMessages([WELCOME]);
  };

  return (
    <>
      {/* Launcher — bottom-left so it never conflicts with the WhatsApp FAB.
          Below md: 44×44 icon-only circle to avoid covering hero CTAs / cards.
          ≥md: full pill with "Ask Biodeal AI" / "Close assistant" label.
          Click/action functionality is identical. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-launcher"
        aria-label={open ? "Close Biodeal AI assistant" : "Open Biodeal AI assistant"}
        aria-expanded={open}
        title={open ? "Close Biodeal AI assistant" : "Ask Biodeal AI"}
        className="fixed bottom-5 left-5 z-[100] inline-flex items-center justify-center gap-2.5 rounded-full bg-bah-ink text-white shadow-2xl ring-1 ring-white/10 transition-all hover:bg-bah-ink/90 hover:-translate-y-0.5 h-12 w-12 md:h-auto md:w-auto md:px-4 md:py-3"
      >
        <span className="relative inline-flex h-5 w-5 items-center justify-center">
          {open ? (
            <X className="h-5 w-5" strokeWidth={2.5} />
          ) : (
            <Bot className="h-5 w-5" strokeWidth={2.5} />
          )}
          {!open && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-bah-ink" />
          )}
        </span>
        <span className="hidden md:inline text-sm font-bold tracking-tight whitespace-nowrap">
          {open ? "Close assistant" : "Ask Biodeal AI"}
        </span>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="ai-chat-panel"
          role="dialog"
          aria-label="Biodeal Animal Health AI assistant"
          className="fixed bottom-24 left-5 right-5 sm:right-auto sm:w-[380px] z-[100] max-h-[75vh] flex flex-col overflow-hidden rounded-2xl bg-white text-bah-ink shadow-2xl ring-1 ring-black/10 animate-in fade-in slide-in-from-bottom-4"
        >
          <header className="flex items-center justify-between gap-2 bg-bah-ink text-white px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
                <Bot className="h-4 w-4" />
              </span>
              <div>
                <div className="text-sm font-bold tracking-tight">
                  Biodeal Animal Health Assistant
                </div>
                <div className="text-[11px] text-white/70">
                  Grounded in our catalogue · not a substitute for a vet
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={resetChat}
              data-testid="ai-chat-reset"
              className="text-[11px] text-white/70 hover:text-white underline underline-offset-2"
              title="Start a new conversation"
            >
              New chat
            </button>
          </header>

          {/* Role picker (only shown until chosen) */}
          {!roleHint && (
            <div className="bg-bah-paper/60 border-b border-black/5 px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-bah-ink/70 mb-2">
                I am a…
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_OPTIONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => chooseRole(r.id)}
                    data-testid={`ai-role-${r.id}`}
                    className="text-xs font-semibold rounded-full border border-bah-ink/15 bg-white px-3 py-1.5 hover:border-bah-ink/50 hover:bg-bah-ink hover:text-white transition-colors"
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Transcript */}
          <div
            ref={scroller}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm bg-white"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 leading-relaxed ${
                    m.role === "user"
                      ? "bg-bah-ink text-white rounded-br-md"
                      : "bg-bah-paper text-bah-ink rounded-bl-md ring-1 ring-black/5"
                  }`}
                  data-testid={`ai-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-2xl bg-bah-paper ring-1 ring-black/5 px-3.5 py-2.5 text-bah-ink/70">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-xs">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={send}
            className="border-t border-black/5 bg-bah-paper/60 px-3 py-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="ai-chat-input"
                rows={1}
                maxLength={1500}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(e);
                  }
                }}
                placeholder="Describe your case, product or need…"
                className="flex-1 resize-none rounded-xl border border-bah-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bah-ink/40"
              />
              <button
                type="submit"
                data-testid="ai-chat-send"
                disabled={loading || !input.trim()}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-bah-ink text-white transition disabled:opacity-40 hover:bg-bah-ink/90"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <a
              href={buildWaLink(
                "Hello Biodeal Animal Health team, I would like to speak to a technical adviser."
              )}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ai-chat-wa-handoff"
              className="mt-2 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-bah-ink/70 hover:text-bah-ink"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Prefer a real person? WhatsApp our team →
            </a>
          </form>
        </div>
      )}
    </>
  );
}
